import { useCustomerDetails, useWealthOverview } from '@egw/api'
import { Tag } from '@egw/components'
import { DEFAULT_CURRENCY_CODE, type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { buildBasicCurrency, Currency, getLastRefreshedText, getCurrencySymbol } from '@egw/utils'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'
import React, { type FC, useEffect, useMemo, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { twMerge } from 'tailwind-merge'

const EGW_ACCOUNTS_OPEN_DEFAULT = true

export const ResponsiveAdviceOverviewPage: FC<{ children: React.ReactNode; onLinkAccounts: () => void }> = ({
  children,
  onLinkAccounts,
}) => {
  const { t } = useTranslation('features-account')
  const { data, isError, isLoading, isFetching, dataUpdatedAt } = useWealthOverview()
  const lastRefreshed = dataUpdatedAt ? getLastRefreshedText({ t, timestamp: dataUpdatedAt }) : undefined
  const navigate = useNavigate()
  const [egwAccountsOpen, setEgwAccountsOpen] = useState<boolean>(EGW_ACCOUNTS_OPEN_DEFAULT)

  const { flags } = useFlags<FeatureFlags>()

  const currencyCode = data?.netWorth.currencyCode ?? DEFAULT_CURRENCY_CODE
  const egwNetworth = data?.netWorth.amount

  const checkingAccounts: any[] = []
  const savingsAccounts: any[] = []
  const investmentAccounts = data?.personalPortfolio?.accounts ?? []

  const allEvergreenAccounts = [...investmentAccounts]

  const {
    data: customerDetails,
    isLoading: isCustomerDetailsLoading,
    isError: isCustomerDetailsError,
  } = useCustomerDetails()

  const linkedExternalInstitutions = useMemo(
    () => customerDetails?.customer.externalAccounts.institutions ?? [],
    [customerDetails?.customer.externalAccounts.institutions],
  )

  const linkedExternalAccounts = useMemo(() => {
    return linkedExternalInstitutions.flatMap((institution) => institution.accounts)
  }, [linkedExternalInstitutions])

  const linkedAccountsOpenDefault = useMemo(() => {
    return Boolean(linkedExternalAccounts.length)
  }, [linkedExternalAccounts.length])

  const [linkedAccountsOpen, setLinkedAccountsOpen] = useState<boolean>(linkedAccountsOpenDefault)

  useEffect(() => {
    const unsubscribe = (): void => {
      setEgwAccountsOpen(EGW_ACCOUNTS_OPEN_DEFAULT)
      setLinkedAccountsOpen(linkedAccountsOpenDefault)
    }
    return unsubscribe
  }, [linkedAccountsOpenDefault])

  const totalNetValueOfAccounts = useMemo(() => {
    if (linkedExternalAccounts.length === 0) return 0
    return linkedExternalAccounts.reduce((acc, account) => acc + (account.externalBalance?.balance ?? 0), 0)
  }, [linkedExternalAccounts])

  const totalAssets = useMemo(() => {
    const hasEgwNetworth = typeof egwNetworth === 'number'
    const hasTotalNetValue = totalNetValueOfAccounts !== 0
    // Both exist (not undefined and not null/0)
    if (hasEgwNetworth && hasTotalNetValue) {
      return egwNetworth + totalNetValueOfAccounts
    }

    // Only egwNetworth exists
    if (hasEgwNetworth && !hasTotalNetValue) {
      return egwNetworth
    }

    // egwNetworth is undefined but totalNetValueOfAccounts exists
    if (!hasEgwNetworth && hasTotalNetValue) {
      return totalNetValueOfAccounts
    }

    // Neither exist
    return undefined
  }, [egwNetworth, totalNetValueOfAccounts])

  return (
    <>
      {isLoading || isCustomerDetailsLoading ? (
        <div className="p-xs bg-primary-500 typo-head-1 egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {isError || isCustomerDetailsError ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('checking.error')}</p>
        </div>
      ) : null}
      {data && customerDetails ? (
        <div className="egw-container">
          <div className="flex w-full justify-end">
            <Tag size="large" className="text-text-meta bg-gray-50 lg:mt-[calc(72px-28px)] border-0">
              <p className="typo-primary-sm-regular">{lastRefreshed}</p>
            </Tag>
          </div>
          <div className={twMerge('overview-card mt-2xs mb-2xl', cx({ 'mb-xs': isFetching }))}>
            {typeof totalAssets === 'number' ? (
              <>
                <h3 className="typo-primary-sm-light text-text-header">{t('overview.your_assets')}</h3>
                <p className="typo-primary-2xl-light text-text-header mb-sm">
                  {buildBasicCurrency({
                    currencyCode,
                    amount: totalAssets,
                  })}
                </p>
              </>
            ) : null}
            <div className="flex flex-col">
              {/* Evergreen Accounts */}

              <div className="flex flex-row gap-sm">
                <button
                  className="h-6 w-6 self-center"
                  type="button"
                  data-testid="overview-egw-accounts-toggle"
                  onClick={() => {
                    setEgwAccountsOpen(!egwAccountsOpen)
                  }}
                >
                  <ChevronDownIcon
                    className={cx('', {
                      'rotate-180 transform duration-100': egwAccountsOpen,
                      'rotate-0 transform duration-100': !egwAccountsOpen,
                    })}
                  />
                </button>

                <button
                  type="button"
                  data-testid="overview-evergreen-accounts-link"
                  onClick={() => void navigate(WEALTH_ROUTES.INVESTMENTS_OVERVIEW.path)}
                  className="flex flex-row w-full justify-between items-center"
                >
                  <p className="typo-primary-md-regular text-text-header">{t('overview.evergreen_accounts')}</p>
                  <p className="typo-primary-lg-regular text-text-body items-center">
                    {typeof egwNetworth === 'number'
                      ? `${getCurrencySymbol(currencyCode)}${Currency.integerCents.onlyMinus(egwNetworth)}`
                      : `${getCurrencySymbol(currencyCode)}0.00`}
                  </p>
                </button>
              </div>
              {egwAccountsOpen
                ? allEvergreenAccounts.map(({ accountNumber, accountName, balance, alias }) => {
                    // Determine account type and get appropriate balance
                    let displayBalance = 0
                    let route = ''
                    if (checkingAccounts.some((account) => account.alias === alias)) {
                      // Bank accounts have balance.total
                      displayBalance = 'total' in balance ? (balance.total as any).amount : 0
                      route = WEALTH_ROUTES.CHECKING_ACCOUNT.path.replace(':alias', alias.toString())
                    } else if (savingsAccounts.some((account) => account.alias === alias)) {
                      // Bank accounts have balance.total
                      displayBalance = 'total' in balance ? (balance.total as any).amount : 0
                      route = WEALTH_ROUTES.SAVINGS_ACCOUNT.path.replace(':alias', alias.toString())
                    } else if (investmentAccounts.some((account) => account.alias === alias)) {
                      // Investment accounts have balance.accountValue
                      displayBalance = 'accountValue' in balance ? (balance.accountValue as any).amount : 0
                      route = WEALTH_ROUTES.INVESTMENTS_ACCOUNT.path.replace(':alias', alias.toString())
                    }

                    return (
                      <button
                        className="py-2xs pl-xl flex flex-row items-center justify-between"
                        type="button"
                        key={accountNumber}
                        data-testid={`overview-account-${alias.toString()}-link`}
                        onClick={() => void navigate(route)}
                      >
                        <p className="typo-primary-base-light text-text-header text-left">{accountName}</p>
                        <p className="typo-primary-md-light text-text-body self-start">
                          {Currency.integerCents.onlyMinus(displayBalance)}
                        </p>
                      </button>
                    )
                  })
                : null}

              {/* Linked Accounts */}
              {flags.rolloutPlaidLinkedAccounts ? (
                <div className="flex flex-col">
                  <div className="flex flex-row gap-sm mt-xs">
                    <button
                      className="h-6 w-6 self-center"
                      type="button"
                      data-testid="overview-linked-accounts-toggle"
                      onClick={() => {
                        setLinkedAccountsOpen(!linkedAccountsOpen)
                      }}
                    >
                      <ChevronDownIcon
                        className={cx('', {
                          'rotate-180 transform duration-100': linkedAccountsOpen,
                          'rotate-0 transform duration-100': !linkedAccountsOpen,
                        })}
                      />
                    </button>

                    <div className="flex flex-row w-full justify-between items-center">
                      <button
                        type="button"
                        data-testid="overview-linked-accounts-link"
                        onClick={() => {
                          void navigate(WEALTH_ROUTES.LINKED_ACCOUNTS.path)
                        }}
                        className="typo-primary-md-regular text-text-header"
                      >
                        {t('overview.linked_accounts')}
                      </button>
                      {linkedExternalAccounts.length === 0 ? (
                        <button
                          type="button"
                          data-testid="overview-link-accounts-button"
                          onClick={() => {
                            onLinkAccounts()
                          }}
                          className="typo-primary-lg-regular text-text-action items-center hover:underline"
                        >
                          {t('overview.link')}
                        </button>
                      ) : (
                        <p className="typo-primary-lg-regular text-text-body items-center">
                          {totalNetValueOfAccounts
                            ? `${getCurrencySymbol(currencyCode)}${Currency.integerCents.onlyMinus(totalNetValueOfAccounts)}`
                            : null}
                        </p>
                      )}
                    </div>
                  </div>
                  {linkedAccountsOpen && linkedExternalAccounts.length === 0 ? (
                    <div className="typo-primary-sm-regular text-text-body ml-xl mt-xs">
                      {t('overview.linked_accounts_description')}
                    </div>
                  ) : null}
                  {linkedAccountsOpen && linkedExternalAccounts.length > 0
                    ? linkedExternalAccounts.map((account) => (
                        <button
                          className="py-2xs pl-xl flex flex-row items-center justify-between"
                          type="button"
                          key={account.alias}
                          data-testid={`overview-linked-account-${account.alias.toString()}-link`}
                          onClick={() => {
                            void navigate(WEALTH_ROUTES.LINKED_ACCOUNTS.path)
                          }}
                        >
                          <p className="typo-primary-base-light text-text-header text-left">{account.name}</p>
                          {typeof account.externalBalance?.balance === 'number' ? (
                            <p className="typo-primary-md-light ml-xs text-text-body self-start">
                              {/* Potentially need to show currency code for these balances as they may be different */}
                              {Currency.integerCents.onlyMinus(account.externalBalance.balance)}
                            </p>
                          ) : null}
                        </button>
                      ))
                    : null}
                </div>
              ) : null}
            </div>
          </div>
          {/* {isFetching ? (
            <div className="w-full justify-items-end mt-xs">
              <RefetchingSpinner />
            </div>
          ) : null} */}
          {children}
        </div>
      ) : null}
    </>
  )
}
